import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  fetchReferential,
  createReferentialItem,
  updateReferentialItem,
  deleteReferentialItem,
  toggleReferentialActive,
  updateTranslation,
  toggleTranslationLock,
  triggerTranslation,
  TRANSLATION_LANGS,
  type RefItem,
} from '../lib/referentialService';
import { logger } from '../lib/logger';

function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

const Certifications: React.FC = () => {
  const [items, setItems] = useState<RefItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const [translationItem, setTranslationItem] = useState<RefItem | null>(null);
  const [translationEdits, setTranslationEdits] = useState<Record<string, string>>({});
  const [savingLang, setSavingLang] = useState<string | null>(null);

  const [batchTranslating, setBatchTranslating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await fetchReferential('certifications'));
    } catch (err) {
      logger.error('load_certifications', 'Certifications', { error: String(err) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) => i.nameFr.toLowerCase().includes(q) || i.code.toLowerCase().includes(q),
    );
  }, [items, search]);

  const untranslatedCount = useMemo(
    () => items.filter((i) => !i.translated).length,
    [items],
  );

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const maxSort = items.reduce((m, i) => Math.max(m, i.sortOrder), 0);
      const created = await createReferentialItem('certifications', {
        code: `cert_${slugify(name)}`,
        nameFr: name,
        nameEn: '',
        nameAr: '',
        nameTr: '',
        nameEs: '',
        namePt: '',
        nameVi: '',
        active: true,
        sortOrder: maxSort + 10,
        translated: false,
        translationLocked: null,
      });
      setItems((prev) => [...prev, created]);
      setNewName('');
    } catch (err) {
      logger.error('create_certification', 'Certifications', { error: String(err) });
      alert(`Erreur lors de la création : ${err instanceof Error ? err.message : err}`);
    } finally {
      setCreating(false);
    }
  };

  const handleRename = async (item: RefItem) => {
    const name = editName.trim();
    setEditingId(null);
    if (!name || name === item.nameFr) return;
    try {
      await updateReferentialItem('certifications', item.id, { nameFr: name });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, nameFr: name } : i)));
    } catch (err) {
      logger.error('rename_certification', 'Certifications', { error: String(err) });
    }
  };

  const handleToggleActive = async (item: RefItem) => {
    try {
      await toggleReferentialActive('certifications', item.id, !item.active);
      setItems((prev) =>
        prev.map((i) => (i.id === item.id ? { ...i, active: !item.active } : i)),
      );
    } catch (err) {
      logger.error('toggle_certification', 'Certifications', { error: String(err) });
    }
  };

  const handleDelete = async (item: RefItem) => {
    if (!window.confirm(`Supprimer « ${item.nameFr} » ? Les diplômes candidats liés seront détachés.`)) return;
    try {
      await deleteReferentialItem('certifications', item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      if (translationItem?.id === item.id) setTranslationItem(null);
    } catch (err) {
      logger.error('delete_certification', 'Certifications', { error: String(err) });
      alert(`Suppression impossible : ${err instanceof Error ? err.message : err}`);
    }
  };

  const openTranslations = (item: RefItem) => {
    setTranslationItem(item);
    setTranslationEdits({
      en: item.nameEn,
      ar: item.nameAr,
      tr: item.nameTr,
      es: item.nameEs,
      pt: item.namePt,
      vi: item.nameVi,
    });
  };

  const saveTranslation = async (lang: string) => {
    if (!translationItem) return;
    setSavingLang(lang);
    try {
      await updateTranslation('certifications', translationItem.id, lang, translationEdits[lang] || '', true);
      await load();
    } catch (err) {
      logger.error('save_certification_translation', 'Certifications', { error: String(err) });
    } finally {
      setSavingLang(null);
    }
  };

  const toggleLock = async (lang: string) => {
    if (!translationItem) return;
    try {
      const locked = !!translationItem.translationLocked?.[lang];
      const newLocks = await toggleTranslationLock('certifications', translationItem.id, lang, !locked);
      setTranslationItem({ ...translationItem, translationLocked: newLocks });
      setItems((prev) =>
        prev.map((i) => (i.id === translationItem.id ? { ...i, translationLocked: newLocks } : i)),
      );
    } catch (err) {
      logger.error('lock_certification_translation', 'Certifications', { error: String(err) });
    }
  };

  const handleBatchTranslate = async () => {
    const ids = items.filter((i) => !i.translated).map((i) => i.id);
    if (ids.length === 0 || batchTranslating) return;
    setBatchTranslating(true);
    try {
      const result = await triggerTranslation('certifications', ids);
      if (result?.error) alert(`Traduction : ${result.error}`);
      await load();
    } finally {
      setBatchTranslating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
        Chargement…
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Certifications et diplômes</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {items.length} entrées · référentiel proposé aux candidats et aux offres
          </p>
        </div>
        {untranslatedCount > 0 && (
          <button
            onClick={handleBatchTranslate}
            disabled={batchTranslating}
            className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-50"
          >
            {batchTranslating ? 'Traduction…' : `Traduire ${untranslatedCount} entrées`}
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher…"
          className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 placeholder-gray-400"
        />
        <div className="flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="Nouvelle certification (nom FR)…"
            className="w-72 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 placeholder-gray-400"
          />
          <button
            onClick={handleCreate}
            disabled={!newName.trim() || creating}
            className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-50"
          >
            Ajouter
          </button>
        </div>
      </div>

      <div className="flex gap-4 items-start">
        {/* Table */}
        <div className="flex-1 bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2.5 font-medium">Nom (FR)</th>
                <th className="px-4 py-2.5 font-medium">Code</th>
                <th className="px-4 py-2.5 font-medium w-24">Traduction</th>
                <th className="px-4 py-2.5 font-medium w-20">Active</th>
                <th className="px-4 py-2.5 font-medium w-28 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr
                  key={item.id}
                  className={`border-t border-gray-100 hover:bg-gray-50 ${translationItem?.id === item.id ? 'bg-blue-50/50' : ''}`}
                >
                  <td className="px-4 py-2.5">
                    {editingId === item.id ? (
                      <input
                        autoFocus
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onBlur={() => handleRename(item)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleRename(item);
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        className="w-full px-2 py-1 border border-[#00244F]/40 rounded text-sm focus:outline-none"
                      />
                    ) : (
                      <span className={item.active ? 'text-gray-800' : 'text-gray-400 line-through'}>
                        {item.nameFr}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-gray-400 font-mono">{item.code}</td>
                  <td className="px-4 py-2.5">
                    {item.translated ? (
                      <span className="inline-flex items-center gap-1 text-xs text-green-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400" /> OK
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-orange-600">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-400" /> À traduire
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => handleToggleActive(item)}
                      className={`w-9 h-5 rounded-full relative transition-colors ${item.active ? 'bg-green-500' : 'bg-gray-300'}`}
                      title={item.active ? 'Désactiver' : 'Activer'}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${item.active ? 'translate-x-4' : 'translate-x-0.5'}`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button
                      onClick={() => openTranslations(item)}
                      title="Voir les traductions"
                      className="px-1.5 text-gray-400 hover:text-[#00244F]"
                    >
                      🌐
                    </button>
                    <button
                      onClick={() => { setEditingId(item.id); setEditName(item.nameFr); }}
                      title="Renommer"
                      className="px-1.5 text-gray-400 hover:text-[#00244F]"
                    >
                      ✏️
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      title="Supprimer"
                      className="px-1.5 text-gray-400 hover:text-red-600"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">
                    Aucune certification.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Translations panel */}
        {translationItem && (
          <div className="w-[360px] bg-white border border-gray-200 rounded-xl p-4 flex-shrink-0 sticky top-4">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-bold text-gray-900">Traductions</h2>
              <button
                onClick={() => setTranslationItem(null)}
                className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                ×
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">{translationItem.nameFr}</p>
            {TRANSLATION_LANGS.map(({ key, label }) => {
              const isLocked = !!translationItem.translationLocked?.[key];
              return (
                <div key={key} className="mb-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {label}
                    {isLocked && <span className="ml-1" title="Verrouillée">🔒</span>}
                  </label>
                  <div className="flex gap-1.5">
                    <input
                      type="text"
                      value={translationEdits[key] || ''}
                      onChange={(e) =>
                        setTranslationEdits((prev) => ({ ...prev, [key]: e.target.value }))
                      }
                      dir={key === 'ar' ? 'rtl' : 'ltr'}
                      className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20"
                    />
                    <button
                      onClick={() => saveTranslation(key)}
                      disabled={savingLang === key}
                      title="Sauvegarder + verrouiller"
                      className="px-2 py-1 text-xs font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg disabled:opacity-50"
                    >
                      {savingLang === key ? '…' : '✓'}
                    </button>
                    <button
                      onClick={() => toggleLock(key)}
                      title={isLocked ? 'Déverrouiller (la prochaine traduction auto écrasera)' : 'Verrouiller (protège des batch)'}
                      className="px-2 py-1 text-xs border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      {isLocked ? '🔓' : '🔒'}
                    </button>
                  </div>
                </div>
              );
            })}
            <button
              onClick={async () => {
                const result = await triggerTranslation('certifications', [translationItem.id]);
                if (result?.error) alert(`Traduction : ${result.error}`);
                await load();
                const fresh = (await fetchReferential('certifications')).find((i) => i.id === translationItem.id);
                if (fresh) openTranslations(fresh);
              }}
              className="w-full mt-2 px-3 py-2 text-xs font-medium text-[#00244F] border border-[#00244F]/30 rounded-lg hover:bg-[#00244F]/5"
            >
              Re-traduire automatiquement
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Certifications;
